import React, { useState, useRef } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaFacebookF,
  FaWhatsapp,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";
import CustomButton from "./Button.jsx";
import api from "../config/axiosConfig.jsx";
import { trackEvent } from "../utils/analytics.js";
import { ORG, VENUE_ADDRESS_LINE } from "../seo/site.js";

// Backend path for contact messages, e.g. "/contact". Until the backend
// provides one, the form opens a pre-filled email to info@wstsc.org.au instead
// of pretending the message was sent.
const CONTACT_ENDPOINT = import.meta.env.VITE_CONTACT_ENDPOINT || "";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  contactNumber: "",
  message: "",
};

const Contactus = ({ showTitle = true }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  const recaptchaRef = useRef(null);
  const messageTextareaRef = useRef(null);

  // Animation variants
  const leftSideVariants = {
    hidden: {
      opacity: 0,
      x: -10,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const rightSideVariants = {
    hidden: {
      opacity: 0,
      x: 10,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  // Validation patterns
  const validationPatterns = {
    // Letters in any script (including Tamil), spaces, hyphens and apostrophes
    firstName: /^[\p{L}\p{M}][\p{L}\p{M}' .-]+$/u,
    lastName: /^[\p{L}\p{M}][\p{L}\p{M}' .-]+$/u,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    contactNumber: /^[0-9]{7,15}$/,
    message: /^.{10,}$/,
  };

  const errorMessages = {
    firstName: "Please enter your first name (at least 2 letters)",
    lastName: "Please enter your last name (at least 2 letters)",
    email: "Please enter a valid email address",
    contactNumber: "Please enter a phone number we can call you on",
    message: "Message must be at least 10 characters long",
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate individual field on blur
    if (value && !validationPatterns[name].test(value)) {
      setErrors((prev) => ({
        ...prev,
        [name]: errorMessages[name],
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle message field focus - show reCAPTCHA
  const handleMessageFocus = () => {
    setShowRecaptcha(true);
  };

  // Handle message field blur - hide reCAPTCHA if empty
  const handleMessageBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate message field
    if (value && !validationPatterns[name].test(value)) {
      setErrors((prev) => ({
        ...prev,
        [name]: errorMessages[name],
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Hide reCAPTCHA if message is empty
    if (!value.trim()) {
      setShowRecaptcha(false);
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    Object.keys(formData).forEach((key) => {
      if (!formData[key].trim()) {
        newErrors[key] = "This field is required";
      } else if (!validationPatterns[key].test(formData[key].trim())) {
        newErrors[key] = errorMessages[key];
      }
    });

    // Check reCAPTCHA if message field has content
    if (formData.message.trim() && !recaptchaValue) {
      newErrors.recaptcha = "Please complete the reCAPTCHA verification";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setTouched({});
    setErrors({});
    setShowRecaptcha(false);
    setRecaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) return;

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const pagePath = window.location.pathname;

    if (!CONTACT_ENDPOINT) {
      const subject = `Website enquiry from ${firstName} ${lastName}`;
      const body = `${formData.message.trim()}\n\nName: ${firstName} ${lastName}\nEmail: ${formData.email.trim()}\nPhone: +${formData.contactNumber}`;
      window.location.href = `mailto:${ORG.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      trackEvent("contact_email_draft", { form_name: "contact", page_path: pagePath });
      setStatus({
        type: "info",
        message: `Your email app should now open with your message to ${ORG.email}. Please press send there to reach us. If nothing opened, email ${ORG.email} or call ${ORG.phones.committee.display}.`,
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.post(CONTACT_ENDPOINT, {
        first_name: firstName,
        last_name: lastName,
        email: formData.email.trim(),
        phone: formData.contactNumber,
        message: formData.message.trim(),
        recaptcha_token: recaptchaValue,
      });
      // Counted only after the backend accepts the message; no form contents sent.
      trackEvent("generate_lead", { form_name: "contact", page_path: pagePath });
      setStatus({
        type: "success",
        message: "Thank you, your message has been sent. We will get back to you soon.",
      });
      resetForm();
    } catch {
      setStatus({
        type: "danger",
        message: `Sorry, your message could not be sent. Please email ${ORG.email} or call ${ORG.phones.committee.display}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showTitle && (
        <section id="contact" className="teamsection p-5">
          <div className="container p-0 mb-5">
            <h2 className="h1 text-center contact-title fw-bold">
              <span className="red-text fw-bold">Contact</span>{" "}
              <span className="green-text position-relative fw-bold">Us</span>
            </h2>
          </div>
        </section>
      )}

      <section className="contact-elevated position-relative mb-5">
        <div className="container overflow-visible">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="contact-card bg-white rounded-5 shadow-sm position-relative overflow-visible">
                <div className="row gx-0">
                  {/* Left: Form with Framer Motion */}
                  <motion.div
                    className="col-lg-8 p-md-5 p-3"
                    variants={leftSideVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <h3 className="h5 fw-bold text-dark mb-2">
                      Send us a message
                    </h3>
                    <p className="small text-dark">
                      Ask us about Tamil classes for your child: levels, class
                      times, fees or how to enrol. We welcome feedback too.
                    </p>

                    <form onSubmit={handleSubmit} noValidate>
                      <div className="row mb-3">
                        <div className="col-md-6 mb-3 mb-md-0">
                          <label htmlFor="contact-firstName" className="form-label small fw-bold text-dark">
                            First Name *
                          </label>
                          <input
                            id="contact-firstName"
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            className={`form-control border rounded-4 custom-input p-2 ${
                              touched.firstName && errors.firstName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter your first name"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {touched.firstName && errors.firstName && (
                            <div className="text-danger small mt-1">
                              {errors.firstName}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="contact-lastName" className="form-label small fw-bold text-dark">
                            Last Name *
                          </label>
                          <input
                            id="contact-lastName"
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            className={`form-control border rounded-4 custom-input p-2 ${
                              touched.lastName && errors.lastName
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Enter your last name"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {touched.lastName && errors.lastName && (
                            <div className="text-danger small mt-1">
                              {errors.lastName}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6 mb-3 mb-md-0">
                          <label htmlFor="contact-email" className="form-label small fw-bold text-dark">
                            Email *
                          </label>
                          <input
                            id="contact-email"
                            type="email"
                            name="email"
                            autoComplete="email"
                            className={`form-control border rounded-4 custom-input p-2 ${
                              touched.email && errors.email ? "is-invalid" : ""
                            }`}
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                          />
                          {touched.email && errors.email && (
                            <div className="text-danger small mt-1">
                              {errors.email}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="contact-phone" className="form-label small fw-bold text-dark">
                            Phone *
                          </label>
                          <PhoneInput
                            country={"au"}
                            placeholder="+61 4xx xxx xxx"
                            inputProps={{
                              id: "contact-phone",
                              name: "contactNumber",
                              autoComplete: "tel",
                            }}
                            value={formData.contactNumber}
                            onChange={(phone) =>
                              setFormData((prev) => ({
                                ...prev,
                                contactNumber: phone,
                              }))
                            }
                            inputClass={`border rounded-4 custom-input p-2 w-100 ${
                              touched.contactNumber && errors.contactNumber
                                ? "is-invalid"
                                : ""
                            }`}
                            containerClass="w-100"
                            dropdownClass="custom-country-dropdown"
                            specialLabel=""
                          />
                          {touched.contactNumber && errors.contactNumber && (
                            <div className="text-danger small mt-1">
                              {errors.contactNumber}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="contact-message" className="form-label small fw-bold text-dark">
                          Message *
                        </label>
                        <textarea
                          id="contact-message"
                          ref={messageTextareaRef}
                          name="message"
                          className={`form-control border rounded-4 custom-input p-2 ${
                            touched.message && errors.message
                              ? "is-invalid"
                              : ""
                          }`}
                          rows="3"
                          placeholder="Enter your message"
                          value={formData.message}
                          onChange={handleInputChange}
                          onFocus={handleMessageFocus}
                          onBlur={handleMessageBlur}
                        ></textarea>
                        {touched.message && errors.message && (
                          <div className="text-danger small mt-1">
                            {errors.message}
                          </div>
                        )}
                      </div>

                      {/* Row: reCAPTCHA (conditional) left, Button always right */}
                      <div className="d-flex align-items-center mb-3 gap-3 flex-wrap justify-content-end">
                        {showRecaptcha && (
                          <div>
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey="6LeEyvgrAAAAALU9ZNkyPpobFU1EdLrj-NAWNzZD"
                              onChange={handleRecaptchaChange}
                            />
                            {errors.recaptcha && (
                              <div className="text-danger small mt-1">
                                {errors.recaptcha}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="ms-auto">
                          <CustomButton
                            colorclassName="red-bg text-white"
                            text={submitting ? "Sending..." : "Send message"}
                            type="submit"
                          />
                        </div>
                      </div>

                      <div role="status" aria-live="polite">
                        {status.message && (
                          <div className={`alert alert-${status.type} small mb-0`}>
                            {status.message}
                          </div>
                        )}
                      </div>
                    </form>
                  </motion.div>

                  {/* Right: Floating contact */}
                  <motion.div
                    className="col-lg-4 d-md-flex position-relative align-items-center justify-content-center"
                    variants={rightSideVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <aside className="contact-float text-white px-4 px-lg-5 py-4 rounded-5 shadow position--lg-absolute">
                      <h3 className="h5 fw-bold mb-3">
                        Prefer to call or email?
                      </h3>

                      <div className="info-box mb-3 p-3 rounded-3 d-flex align-items-center">
                        <FaPhoneAlt className="me-3 fs-5" />
                        <div>
                          <span className="fw-semibold">Committee Number:</span>
                          <br />
                          <a
                            href={`tel:${ORG.phones.committee.tel}`}
                            className="fw-bold text-white text-decoration-none"
                          >
                            {ORG.phones.committee.display}
                          </a>
                        </div>
                      </div>

                      <div className="info-box mb-3 p-3 rounded-3 d-flex align-items-center">
                        <FaPhoneAlt className="me-3 fs-5" />
                        <div>
                          Principal Number
                          <br />
                          <a
                            href={`tel:${ORG.phones.principal.tel}`}
                            className="fw-bold text-white text-decoration-none"
                          >
                            {ORG.phones.principal.display}
                          </a>
                        </div>
                      </div>

                      <div className="info-box mb-3 p-3 rounded-3 d-flex align-items-center">
                        <FaEnvelope className="me-3 fs-5" />
                        <div>
                          Email:
                          <br />
                          <a
                            href="mailto:info@wstsc.org.au"
                            className="fw-bold text-white text-decoration-none"
                          >
                            info@wstsc.org.au
                          </a>
                        </div>
                      </div>

                      <div className="info-box mb-3 p-3 rounded-3 d-flex align-items-center">
                        <MdLocationOn className="me-3 fs-3" />
                        <div>
                          Location:
                          <br />
                          <a
                            href={ORG.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fw-bold text-white text-decoration-none"
                          >
                            {ORG.venue.name}, {VENUE_ADDRESS_LINE}
                          </a>
                        </div>
                      </div>

                      {/* <div className="mt-3 pt-3">
                        <h6 className="fw-bold">Connect with us</h6>
                        <div className="d-flex gap-4 mt-2">
                          <a
                            href="https://www.facebook.com/"
                            className="text-white"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaFacebookF />
                          </a>

                          <a
                            href="https://www.instagram.com/"
                            className="text-white"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaInstagram />
                          </a>

                          <a
                            href="https://www.youtube.com/"
                            className="text-white"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaYoutube />
                          </a>

                          <a
                            href="https://wa.me/"
                            className="text-white"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaWhatsapp />
                          </a>
                        </div>
                      </div> */}
                    </aside>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contactus;
